/**
 * Permission hook for checking user permissions in components.
 *
 * Reads the authenticated user's permissions, dataScope, and companyRoleCode
 * from the auth store and exposes utility helpers for permission checks.
 */

import { useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';

export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  const permissions: string[] = user?.permissions ?? [];
  const dataScope: string = user?.dataScope ?? 'ALL';
  const companyRoleCode: string = user?.companyRoleCode ?? '';
  const role: string = user?.role ?? '';

  // Stable key for memoization (array references change on every render)
  const permissionsKey = permissions.join(',');

  /**
   * Check whether the user holds a specific permission string.
   * Supports the '*' wildcard -- a user whose permissions list contains '*'
   * is treated as having every permission.
   */
  const hasPermission = useMemo(() => {
    const permSet = new Set(permissions);
    return (permission: string): boolean => {
      if (permSet.has('*')) return true;
      return permSet.has(permission);
    };
  }, [permissionsKey]);

  const hasAnyPermission = useMemo(() => {
    const permSet = new Set(permissions);
    return (perms: string[]): boolean => {
      if (permSet.has('*')) return true;
      return perms.some((p) => permSet.has(p));
    };
  }, [permissionsKey]);

  const canAccessModule = useMemo(() => {
    return (module: string): boolean => {
      if (permissions.includes('*')) return true;
      const prefix = module.endsWith(':') ? module : `${module}:`;
      return permissions.some((p) => p.startsWith(prefix));
    };
  }, [permissionsKey]);

  // Convenience role booleans (use companyRoleCode which is the new role system)
  const effectiveRole = companyRoleCode || role;
  const isOwner = effectiveRole === 'OWNER' || effectiveRole === 'SUPER_ADMIN';
  const isAdmin = effectiveRole === 'ADMIN' || isOwner;
  const isManager = effectiveRole === 'MANAGER' || isAdmin;
  const isSalesRep = effectiveRole === 'SALES_REP';
  const isViewer = effectiveRole === 'VIEWER';

  return {
    permissions,
    dataScope,
    companyRoleCode,
    hasPermission,
    hasAnyPermission,
    canAccessModule,
    isOwner,
    isAdmin,
    isManager,
    isSalesRep,
    isViewer,
  };
}
